<template>
  <div style="width: 100%; padding: 10px">
    <div style="display: flex; justify-content: space-between; align-items: center;">
      <h1 style="font-size: 24px; margin: 0;">{{ $t("fitness.header") }} - {{ $t("timeline.monthly_view") }}</h1>
      <v-btn
        text
        :loading="l_import"
        :disabled="l_import"
        color="default"
        @click="openRangeImport"
      >
        <i class="fad fa-file-import" style="margin-right: 5px"></i>
        <span class="hidden-xs-only">{{ $t("fitness.import_date_range_title") }}</span>
      </v-btn>
    </div>
    <p style="margin-top: 5px; text-align: center">
      <v-btn @click="monthBack" text>
        <i class="fal fa-chevron-left"></i>
      </v-btn>
      <v-btn text @click="d_month_select = true">
        <i
          style="margin-left: 5px; cursor: pointer"
          class="fad fa-calendar-alt"
        ></i>
      </v-btn>
      <v-btn @click="monthForward" :disabled="currentMonthStr === todayMonthStr" text>
        <i class="fal fa-chevron-right"></i>
      </v-btn>
      <br>
      <span style="font-size:23px;">{{ formattedMonth }}</span>
    </p>

    <!-- Monthly Stats -->
    <v-layout
      row
      wrap
      style="margin-left: 0; margin-right: 0; margin-top: 20px"
    >
      <info-tile
        :name="$t('fitness.steps')"
        :data="total_steps_formatted"
        :loading="l_data"
        icon="shoe-prints"
      ></info-tile>
      <info-tile
        name="Average Steps"
        :data="avg_steps_formatted"
        :loading="l_data"
        icon="shoe-prints"
      ></info-tile>
      <info-tile
        :name="$t('fitness.calories')"
        :data="total_calories_formatted"
        :loading="l_data"
        icon="flame"
      ></info-tile>
      <info-tile
        :name="$t('fitness.floors')"
        :data="total_floors_formatted"
        :loading="l_data"
        icon="level-up"
      ></info-tile>
      <info-tile
        name="Average Sleep"
        :data="avg_sleep_formatted"
        :loading="l_data"
        icon="moon"
      ></info-tile>
      <info-tile
        :name="$t('fitness.distance')"
        :data="total_distance_formatted ? total_distance_formatted + ' km' : null"
        :loading="l_data"
        icon="map"
      ></info-tile>
      <info-tile
        :name="$t('fitness.weight')"
        :data="avg_weight_formatted"
        :loading="l_data"
        icon="weight"
      ></info-tile>
      <info-tile
        :name="$t('fitness.body_fat')"
        :data="avg_body_fat_formatted"
        :loading="l_data"
        icon="percentage"
      ></info-tile>
      <info-tile
        :name="$t('fitness.muscle_mass')"
        :data="avg_muscle_mass_formatted"
        :loading="l_data"
        icon="dumbbell"
      ></info-tile>
      <info-tile
        :name="$t('fitness.bone_mass')"
        :data="avg_bone_mass_formatted"
        :loading="l_data"
        icon="bone"
      ></info-tile>
      <info-tile
        :name="$t('fitness.hydration')"
        :data="avg_hydration_formatted"
        :loading="l_data"
        icon="tint"
      ></info-tile>
      <info-tile
        :name="$t('fitness.spo2')"
        :data="avg_spo2_formatted"
        :loading="l_data"
        icon="lungs"
      ></info-tile>
      <info-tile
        :name="$t('fitness.body_temperature')"
        :data="avg_body_temperature_formatted"
        :loading="l_data"
        icon="thermometer-half"
      ></info-tile>
    </v-layout>

    <v-layout row wrap style="margin: 5px; margin-top: 20px;">
        <v-flex xs12 sm12>
            <div v-if="l_data">
                <v-skeleton-loader type="image" height="350"></v-skeleton-loader>
            </div>
            <div v-else-if="chartOptions.xaxis.categories.length > 0">
                <apexchart type="bar" height="350" :options="chartOptions" :series="series"></apexchart>
            </div>
            <div v-else style="text-align: center; margin-top: 20px;">
                <p>{{ $t("fitness.no_data_here") }}</p>
            </div>
        </v-flex>
    </v-layout>

    <v-dialog v-model="d_month_select" max-width="400">
      <v-card>
        <v-card-title class="headline">Select Month</v-card-title>
        <v-card-text style="text-align: center">
          <v-date-picker
            v-model="pickerMonth"
            type="month"
            :max="todayMonthStr"
            @change="selectMonth"
          ></v-date-picker>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="d_range_import" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">{{ $t("fitness.import_date_range_title") }}</v-card-title>
        <v-card-text style="text-align: center" v-if="!rangeProgress.active">
          <v-date-picker
            v-model="range_dates"
            range
            :first-day-of-week="1"
            :allowed-dates="allowedDates"
            :picker-date.sync="rangePickerDate"
            style="width: 100%; margin-top: 10px;"
          ></v-date-picker>
        </v-card-text>
        <v-card-text v-else style="padding-top: 20px; padding-bottom: 30px;">
          <div class="mb-2 text-center" style="font-size: 16px; font-weight: bold;">
             {{ rangeProgress.current }} / {{ rangeProgress.total }}
          </div>
          <v-progress-linear
            :value="(rangeProgress.current / rangeProgress.total) * 100"
            height="20"
            color="primary"
          ></v-progress-linear>
          <p class="mt-4 text-center" style="font-size: 14px; color: grey; margin-bottom: 0;">
             {{ $t('fitness.importing_progress', { current: rangeProgress.current, total: rangeProgress.total, date: rangeProgress.date }) }}
          </p>
        </v-card-text>
        <v-card-actions v-if="!rangeProgress.active">
          <v-spacer></v-spacer>
          <v-btn text @click="d_range_import = false">{{ $t("general.cancel") }}</v-btn>
          <v-btn color="primary" @click="startImportRange" :disabled="range_dates.length === 0">{{ $t("fitness.import") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import VueApexCharts from 'vue-apexcharts';
import axios from "axios";
import { storage } from "../schmucklicloud";
import { Notifications } from "../notification";
import InfoTile from "../components/fitness/InfoTile.vue";
var numeral = require("numeral");

Vue.use(VueApexCharts);
Vue.component('apexchart', VueApexCharts);

export default {
  data() {
    return {
      l_data: true,
      current_date: new Date(),
      
      d_month_select: false,
      pickerMonth: null,

      fitness_data: [],

      l_import: false,
      withings_auth_attempt: 0,
      d_range_import: false,
      range_dates: [],
      rangePickerDate: null,
      rangeProgress: { active: false, current: 0, total: 0, date: '' },
      
      total_steps: 0,
      steps_days: 0,
      total_calories: 0,
      calories_days: 0,
      total_distance: 0,
      distance_days: 0,
      total_floors: 0,
      floors_days: 0,
      total_sleep_minutes: 0,
      sleep_days: 0,
      total_weight: 0,
      weight_days: 0,
      total_body_fat: 0,
      body_fat_days: 0,
      total_muscle_mass: 0,
      muscle_mass_days: 0,
      total_bone_mass: 0,
      bone_mass_days: 0,
      total_hydration: 0,
      hydration_days: 0,
      total_spo2: 0,
      spo2_days: 0,
      total_body_temperature: 0,
      body_temperature_days: 0,
      
      series: [{
        name: 'Steps',
        data: []
      }],
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
              show: false
          },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              let day = this.chartOptions.xaxis.categories[config.dataPointIndex];
              if (day) {
                let dateStr = this.currentMonthStr + "-" + day;
                this.$router.push('/dashboard/' + dateStr);
              }
            }
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          title: {
            text: 'Steps'
          }
        },
        fill: {
          opacity: 1,
          colors: ['#FFB024']
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return numeral(val).format("0,000").replace(/,/g, "'") + " steps"
            }
          }
        }
      }
    };
  },
  components: {
    InfoTile,
  },
  mounted() {
    var query_date = this.$route.params.date;
    if (query_date !== undefined) {
      this.current_date = new Date(query_date + "-01");
    } else {
      this.updateUrl();
    }
    this.pickerMonth = this.currentMonthStr;
    this.loadData();
  },
  computed: {
    currentMonthStr() {
      return (
        this.current_date.getFullYear() +
        "-" +
        (this.current_date.getMonth() + 1 + "").padStart(2, "0")
      );
    },
    todayMonthStr() {
      var today = new Date();
      return (
        today.getFullYear() +
        "-" +
        (today.getMonth() + 1 + "").padStart(2, "0")
      );
    },
    formattedMonth() {
      var months = [
        this.$t("general.month.january"),
        this.$t("general.month.february"),
        this.$t("general.month.march"),
        this.$t("general.month.april"),
        this.$t("general.month.may"),
        this.$t("general.month.june"),
        this.$t("general.month.july"),
        this.$t("general.month.august"),
        this.$t("general.month.september"),
        this.$t("general.month.october"),
        this.$t("general.month.november"),
        this.$t("general.month.december"),
      ];
      return months[this.current_date.getMonth()] + " " + this.current_date.getFullYear();
    },
    total_steps_formatted() {
        return this.total_steps > 0 ? numeral(this.total_steps).format("0,000").replace(/,/g, "'") : null;
    },
    avg_steps_formatted() {
        if (this.steps_days === 0) return null;
        return numeral(this.total_steps / this.steps_days).format("0,000").replace(/,/g, "'");
    },
    total_calories_formatted() {
        return this.total_calories > 0 ? numeral(this.total_calories).format("0,000").replace(/,/g, "'") : null;
    },
    total_distance_formatted() {
        return this.total_distance > 0 ? numeral(this.total_distance).format("0.00").replace(/,/g, "'") : null;
    },
    total_floors_formatted() {
        return this.total_floors > 0 ? numeral(this.total_floors).format("0,000").replace(/,/g, "'") : null;
    },
    avg_sleep_formatted() {
        if (this.sleep_days === 0) return null;
        let avg_minutes = this.total_sleep_minutes / this.sleep_days;
        return this.getHoursAndMinutesFromMinutes(avg_minutes);
    },
    avg_weight_formatted() {
        return this.weight_days > 0 ? numeral(this.total_weight / this.weight_days).format("0.00").replace(/,/g, "'") + " kg" : null;
    },
    avg_body_fat_formatted() {
        return this.body_fat_days > 0 ? numeral(this.total_body_fat / this.body_fat_days).format("0.00").replace(/,/g, "'") + " %" : null;
    },
    avg_muscle_mass_formatted() {
        return this.muscle_mass_days > 0 ? numeral(this.total_muscle_mass / this.muscle_mass_days).format("0.00").replace(/,/g, "'") + " kg" : null;
    },
    avg_bone_mass_formatted() {
        return this.bone_mass_days > 0 ? numeral(this.total_bone_mass / this.bone_mass_days).format("0.00").replace(/,/g, "'") + " kg" : null;
    },
    avg_hydration_formatted() {
        return this.hydration_days > 0 ? numeral(this.total_hydration / this.hydration_days).format("0.00").replace(/,/g, "'") + " kg" : null;
    },
    avg_spo2_formatted() {
        return this.spo2_days > 0 ? numeral(this.total_spo2 / this.spo2_days).format("0.00").replace(/,/g, "'") + " %" : null;
    },
    avg_body_temperature_formatted() {
        return this.body_temperature_days > 0 ? numeral(this.total_body_temperature / this.body_temperature_days).format("0.00").replace(/,/g, "'") + " °C" : null;
    }
  },
  methods: {
    getHoursAndMinutesFromMinutes(input_minutes) {
      var minutes = parseInt(input_minutes);
      var hours = Math.floor(minutes / 60);
      minutes = minutes % 60;
      return hours + " hrs " + minutes + " mins";
    },
    monthBack() {
      this.current_date = new Date(this.current_date.getFullYear(), this.current_date.getMonth() - 1, 1);
      this.pickerMonth = this.currentMonthStr;
      this.updateUrl();
      this.loadData();
    },
    monthForward() {
      this.current_date = new Date(this.current_date.getFullYear(), this.current_date.getMonth() + 1, 1);
      this.pickerMonth = this.currentMonthStr;
      this.updateUrl();
      this.loadData();
    },
    allowedDates(val) {
      var date = new Date(val);
      return new Date() - date > 0;
    },
    async startImportWithings(targetDate, silent = false, isRetry = false) {
      if (typeof targetDate !== "string") return;
      this.l_import = true;
      var token_object = localStorage.getItem("withings_auth") || undefined;
      if (token_object === undefined) {
        try {
          await this.authenticateWithings();
          return await this.startImportWithings(targetDate, silent, true);
        } catch (e) {
          this.l_import = false;
          throw new Error("Authentication required");
        }
      } else {
        var access_token = JSON.parse(token_object).body.access_token;

        try {
          var existing = await storage.get("fitness_daily", [
            { column: "date", operator: "==", value: targetDate },
          ]);
          if (existing.isOK && existing.data && existing.data.length > 0) {
            await storage.delete("fitness_daily", existing.data[0].id);
          }

          var body = new FormData();
          body.append("action", "getactivity");
          body.append(
            "data_fields",
            "steps,elevation,totalcalories,distance,hr_average"
          );
          body.append("category", 2);
          body.append("startdateymd", targetDate);
          body.append("enddateymd", targetDate);

          var response_metrics = await axios({
            url: "https://wbsapi.withings.net/v2/measure",
            method: "post",
            data: body,
            headers: {
              Authorization: "Bearer " + access_token,
              "Content-Type": "multipart/form-data",
            },
          });

          if (response_metrics.data.status !== 0) {
            throw new Error("API returned status " + response_metrics.data.status);
          }

          var data_steps = undefined;
          if (response_metrics.data.body && response_metrics.data.body.activities && response_metrics.data.body.activities.length > 0) {
            data_steps = response_metrics.data.body.activities[0];
          }

          var sC_response_steps = await storage.insert("fitness_daily", {
            date: targetDate,
            steps: data_steps ? data_steps.steps : 0,
            calories: data_steps ? Math.floor(data_steps.totalcalories) : 0,
            distance: data_steps ? data_steps.distance / 1000 : 0,
            floors: data_steps ? data_steps.elevation : 0,
            heart_rate: data_steps && data_steps.hr_average ? data_steps.hr_average : 0,
            imported_from: "withings",
          });

          if (!sC_response_steps.isOK) {
            Notifications.show(
              "There was a problem while importing the basic data."
            );
            throw new Error("Could not insert basic data");
          }

          var rowset_id = sC_response_steps.data.id;

          try {
            var sleep_body = new FormData();
            sleep_body.append("action", "getsummary");
            sleep_body.append(
              "data_fields",
              "durationtosleep,durationtowakeup,lightsleepduration,remsleepduration,deepsleepduration,wakeupduration"
            );
            sleep_body.append("startdateymd", targetDate);
            sleep_body.append("enddateymd", targetDate);

            var response_sleep = await axios({
              url: "https://wbsapi.withings.net/v2/sleep",
              method: "post",
              data: sleep_body,
              headers: {
                Authorization: "Bearer " + access_token,
                "Content-Type": "multipart/form-data",
              },
            });

            var data_sleep = undefined;
            if (response_sleep.data.status === 0 && response_sleep.data.body && response_sleep.data.body.series && response_sleep.data.body.series.length > 0) {
              data_sleep = response_sleep.data.body.series[0].data;
            }

            var sleep_duration = 0,
              sleep_total = 0;

            if (data_sleep) {
              sleep_duration += data_sleep.deepsleepduration || 0;
              if (data_sleep.lightsleepduration) {
                sleep_duration += data_sleep.lightsleepduration;
              }
              if (data_sleep.remsleepduration) {
                sleep_duration += data_sleep.remsleepduration;
              }

              sleep_total += sleep_duration;
              sleep_total += data_sleep.durationtosleep || 0;
              sleep_total += data_sleep.durationtowakeup || 0;
              if (data_sleep.wakeupduration) {
                sleep_total += data_sleep.wakeupduration;
              }
            }

            var sC_response_sleep = await storage.update(
              "fitness_daily",
              rowset_id,
              {
                sleep_duration: sleep_duration / 60, // To minutes
                sleep_total: sleep_total / 60,
              }
            );

            if (!sC_response_sleep.isOK) {
              Notifications.show(
                "There was a problem while importing the sleep data."
              );
            }
          } catch (e) {
            console.warn("Could not pull sleep data:", e);
          }

          // Body composition data
          try {
            var startdate = Math.floor(new Date(targetDate + "T00:00:00").getTime() / 1000);
            var enddate = Math.floor(new Date(targetDate + "T23:59:59").getTime() / 1000);

            var meas_body = new FormData();
            meas_body.append("action", "getmeas");
            meas_body.append("category", 1);
            meas_body.append("startdate", startdate);
            meas_body.append("enddate", enddate);

            var response_meas = await axios({
              url: "https://wbsapi.withings.net/measure",
              method: "post",
              data: meas_body,
              headers: {
                Authorization: "Bearer " + access_token,
                "Content-Type": "multipart/form-data",
              },
            });

            var data_meas = {
              weight: 0,
              body_fat: 0,
              muscle_mass: 0,
              bone_mass: 0,
              hydration: 0,
              spo2: 0,
              body_temperature: 0
            };

            if (response_meas.data.status === 0 && response_meas.data.body && response_meas.data.body.measuregrps) {
              for (let grp of response_meas.data.body.measuregrps) {
                for (let measure of grp.measures) {
                  let value = measure.value * Math.pow(10, measure.unit);
                  if (measure.type === 1) data_meas.weight = value; // Weight (kg)
                  if (measure.type === 6) data_meas.body_fat = value; // Fat Ratio (%)
                  if (measure.type === 76) data_meas.muscle_mass = value; // Muscle Mass (kg)
                  if (measure.type === 88) data_meas.bone_mass = value; // Bone Mass (kg)
                  if (measure.type === 77) data_meas.hydration = value; // Hydration (kg)
                  if (measure.type === 54) data_meas.spo2 = value; // SpO2 (%)
                  if (measure.type === 71) data_meas.body_temperature = value; // Body Temperature (°C)
                  if (measure.type === 73 && !data_meas.body_temperature) data_meas.body_temperature = value; // Skin Temperature (°C)
                  if (measure.type === 12 && !data_meas.body_temperature) data_meas.body_temperature = value; // Temperature (°C)
                }
              }

              if (data_meas.weight > 0) data_meas.weight = Math.round(data_meas.weight * 100) / 100;
              if (data_meas.body_fat > 0) data_meas.body_fat = Math.round(data_meas.body_fat * 100) / 100;
              if (data_meas.muscle_mass > 0) data_meas.muscle_mass = Math.round(data_meas.muscle_mass * 100) / 100;
              if (data_meas.bone_mass > 0) data_meas.bone_mass = Math.round(data_meas.bone_mass * 100) / 100;
              if (data_meas.hydration > 0) data_meas.hydration = Math.round(data_meas.hydration * 100) / 100;
              if (data_meas.spo2 > 0) data_meas.spo2 = Math.round(data_meas.spo2 * 100) / 100;
              if (data_meas.body_temperature > 0) data_meas.body_temperature = Math.round(data_meas.body_temperature * 100) / 100;

              if (data_meas.weight > 0 || data_meas.body_fat > 0 || data_meas.muscle_mass > 0 || data_meas.bone_mass > 0 || data_meas.hydration > 0 || data_meas.spo2 > 0 || data_meas.body_temperature > 0) {
                var sC_response_meas = await storage.update(
                  "fitness_daily",
                  rowset_id,
                  data_meas
                );

                if (!sC_response_meas.isOK) {
                  Notifications.show(
                    "There was a problem while importing the body composition data."
                  );
                }
              }
            } else if (response_meas.data.status !== 0) {
              console.warn("API returned status " + response_meas.data.status + " for body composition. Re-authentication may be required.");
            }
          } catch (e) {
            console.warn("Could not pull body composition data:", e);
          }

          if (!silent) {
            Notifications.show(
              "The data for " +
                targetDate +
                " has been imported from Withings."
            );
          }
          this.withings_auth_attempt = 0;
          this.l_import = false;
          if (targetDate.startsWith(this.currentMonthStr)) this.loadData();
          return true;
        } catch (e) {
          console.error(e);
          if (e.message && e.message.includes("status 401")) {
            if (!isRetry) {
              try {
                await this.authenticateWithings();
                return await this.startImportWithings(targetDate, silent, true);
              } catch (authError) {
                this.l_import = false;
                if (!silent) Notifications.show("Withings import failed: " + e.message);
                throw authError;
              }
            }
          }
          
          this.l_import = false;
          if (!silent) Notifications.show("Withings import failed: " + e.message);
          throw e;
        }
      }
    },
    async startImportRange() {
      if (!this.range_dates || this.range_dates.length === 0) return;
      
      let dates = [];
      let d1 = new Date(this.range_dates[0]);
      let d2 = new Date(this.range_dates[this.range_dates.length - 1]);
      
      if (d1 > d2) {
        let temp = d1;
        d1 = d2;
        d2 = temp;
      }
      
      let curr = new Date(d1);
      while(curr <= d2) {
        dates.push(curr.getFullYear() + "-" + (curr.getMonth() + 1 + "").padStart(2, "0") + "-" + (curr.getDate() + "").padStart(2, "0"));
        curr.setDate(curr.getDate() + 1);
      }
      
      let successCount = 0;
      this.rangeProgress = { active: true, current: 0, total: dates.length, date: '' };
      
      for (let i = 0; i < dates.length; i++) {
        let dateToSync = dates[i];
        this.rangeProgress.current = i + 1;
        let dParts = dateToSync.split('-');
        this.rangeProgress.date = new Date(parseInt(dParts[0]), parseInt(dParts[1]) - 1, parseInt(dParts[2])).toLocaleDateString('de-CH');
        
        try {
          await this.startImportWithings(dateToSync, true);
          successCount++;
        } catch (err) {
          console.error("Range import failed on date", dateToSync, err);
          Notifications.show(this.$t("fitness.import_failed", { date: dateToSync }));
          break;
        }
      }
      
      this.rangeProgress.active = false;
      if (successCount > 0) {
        Notifications.show(this.$t("fitness.import_success", { count: successCount }));
      }
      this.d_range_import = false;
      this.loadData();
    },
    authenticateWithings() {
      return new Promise((resolve, reject) => {
        (async () => {
          if (this.withings_auth_attempt >= 2) {
            Notifications.show("Withings import failed: Could not authorize after multiple attempts.");
            this.l_import = false;
            this.withings_auth_attempt = 0;
            reject(new Error("Could not authorize"));
            return;
          }

          var withings_auth = localStorage.getItem("withings_auth") || undefined;
          if (withings_auth !== undefined && this.withings_auth_attempt === 0) {
            this.withings_auth_attempt++;

            if (!JSON.parse(withings_auth).body.refresh_token) {
              localStorage.removeItem("withings_auth");
              reject(new Error("No refresh token available"));
              return;
            }

            try {
              var response = await axios.get(
                "https://func-chn-tracky.azurewebsites.net/api/TrackyWithingsAuth?code=hpPOraZ9KElAHGcjYIteKopToNiH9-_Jw986IXVg3SCwAzFu1E1P6w==&" +
                  new URLSearchParams({
                    refresh_token: JSON.parse(withings_auth).body.refresh_token,
                    function: "refresh_token",
                  }).toString()
              );

              if (response.status === 200) {
                if (response.data.status !== 0) {
                  localStorage.removeItem("withings_auth");
                  reject(new Error("Token refresh failed"));
                } else {
                  localStorage.setItem(
                    "withings_auth",
                    JSON.stringify(response.data)
                  );
                  resolve();
                }
              } else {
                 localStorage.removeItem("withings_auth");
                 reject(new Error("Token refresh failed with status " + response.status));
              }
            } catch(e) {
              localStorage.removeItem("withings_auth");
              reject(e);
            }
          } else {
            this.withings_auth_attempt++;
            var withings_window = window.open(
              "https://account.withings.com/oauth2_user/authorize2?client_id=87231d61e5ea317f7d0fb6eb1328210042bbf989a2c1084904bb0f9a3406b491&redirect_uri=https://tracky.schmuckli.dev/fitness/import_withings.html&response_type=code&scope=user.activity,user.metrics&state=" +
                new Date().getTime(),
              "withings-sign-in",
              "height=700,width=550"
            );

            if (!withings_window) {
              Notifications.show("Please allow popups to sign in to Withings.");
              this.l_import = false;
              reject(new Error("Popup blocked"));
              return;
            }

            var checkWithingsTimer = setInterval(function () {
              if (withings_window.closed) {
                clearInterval(checkWithingsTimer);
                resolve();
              }
            }.bind(this), 500);
          }
        })();
      });
    },
    selectMonth(monthStr) {
      this.current_date = new Date(monthStr + "-01");
      this.d_month_select = false;
      this.updateUrl();
      this.loadData();
    },
    openRangeImport() {
      let year = this.current_date.getFullYear();
      let month = (this.current_date.getMonth() + 1).toString().padStart(2, '0');
      let lastDay = new Date(year, this.current_date.getMonth() + 1, 0).getDate();
      this.rangePickerDate = `${year}-${month}`;
      this.range_dates = [`${year}-${month}-01`, `${year}-${month}-${lastDay}`];
      this.d_range_import = true;
    },
    updateUrl() {
      if (this.$route.params.date !== this.currentMonthStr) {
        this.$router.replace('/fitness_month/' + this.currentMonthStr).catch(() => {});
      }
    },
    async loadData() {
      this.l_data = true;
      var response = await storage.get("fitness_daily", [
        {
          column: "date",
          operator: "contains",
          value: this.currentMonthStr,
        },
      ]);

      this.fitness_data = (response.isOK && response.data) ? response.data.sort((a, b) => a.data.date.localeCompare(b.data.date)) : [];

      let steps = 0, steps_days_count = 0;
      let cals = 0, cals_days_count = 0;
      let dist = 0, dist_days_count = 0;
      let floors = 0, floors_days_count = 0;
      let sleep_mins = 0;
      let sleep_days_count = 0;
      let weight = 0, weight_days_count = 0;
      let body_fat = 0, body_fat_days_count = 0;
      let muscle_mass = 0, muscle_mass_days_count = 0;
      let bone_mass = 0, bone_mass_days_count = 0;
      let hydration = 0, hydration_days_count = 0;
      let spo2 = 0, spo2_days_count = 0;
      let body_temperature = 0, body_temperature_days_count = 0;

      let year = parseInt(this.currentMonthStr.split("-")[0]);
      let month = parseInt(this.currentMonthStr.split("-")[1]);
      let daysInMonth = new Date(year, month, 0).getDate();

      let categories = [];
      let seriesData = [];

      let dayDataMap = {};
      this.fitness_data.forEach(item => {
          let dayData = item.data;
          let day = dayData.date.split("-")[2];
          dayDataMap[day] = dayData;
      });

      for (let i = 1; i <= daysInMonth; i++) {
          let dayStr = i.toString().padStart(2, '0');
          categories.push(dayStr);
          
          if (dayDataMap[dayStr]) {
              let dayData = dayDataMap[dayStr];
              let s = parseInt(dayData.steps);
              let c = parseInt(dayData.calories);
              let d = parseFloat(dayData.distance);
              let f = parseInt(dayData.floors);
              let sleep = parseInt(dayData.sleep_duration);

              if (!isNaN(s) && s > 0) { steps += s; steps_days_count++; }
              if (!isNaN(c) && c > 0) { cals += c; cals_days_count++; }
              if (!isNaN(d) && d > 0) { dist += d; dist_days_count++; }
              if (!isNaN(f) && f > 0) { floors += f; floors_days_count++; }
              if (!isNaN(sleep) && sleep > 0) {
                  sleep_mins += sleep;
                  sleep_days_count += 1;
              }
              
              let w = parseFloat(dayData.weight);
              if (!isNaN(w) && w > 0) { weight += w; weight_days_count++; }
              let bf = parseFloat(dayData.body_fat);
              if (!isNaN(bf) && bf > 0) { body_fat += bf; body_fat_days_count++; }
              let mm = parseFloat(dayData.muscle_mass);
              if (!isNaN(mm) && mm > 0) { muscle_mass += mm; muscle_mass_days_count++; }
              let bm = parseFloat(dayData.bone_mass);
              if (!isNaN(bm) && bm > 0) { bone_mass += bm; bone_mass_days_count++; }
              let h = parseFloat(dayData.hydration);
              if (!isNaN(h) && h > 0) { hydration += h; hydration_days_count++; }
              let sp = parseFloat(dayData.spo2);
              if (!isNaN(sp) && sp > 0) { spo2 += sp; spo2_days_count++; }
              let bt = parseFloat(dayData.body_temperature);
              if (!isNaN(bt) && bt > 0) { body_temperature += bt; body_temperature_days_count++; }
              
              seriesData.push(isNaN(s) || s === 0 ? null : s);
          } else {
              seriesData.push(null);
          }
      }

      this.total_steps = steps;
      this.steps_days = steps_days_count;
      this.total_calories = cals;
      this.calories_days = cals_days_count;
      this.total_distance = dist;
      this.distance_days = dist_days_count;
      this.total_floors = floors;
      this.floors_days = floors_days_count;
      this.total_sleep_minutes = sleep_mins;
      this.sleep_days = sleep_days_count;
      this.total_weight = weight;
      this.weight_days = weight_days_count;
      this.total_body_fat = body_fat;
      this.body_fat_days = body_fat_days_count;
      this.total_muscle_mass = muscle_mass;
      this.muscle_mass_days = muscle_mass_days_count;
      this.total_bone_mass = bone_mass;
      this.bone_mass_days = bone_mass_days_count;
      this.total_hydration = hydration;
      this.hydration_days = hydration_days_count;
      this.total_spo2 = spo2;
      this.spo2_days = spo2_days_count;
      this.total_body_temperature = body_temperature;
      this.body_temperature_days = body_temperature_days_count;

      this.chartOptions = {
          ...this.chartOptions,
          xaxis: {
              categories: categories
          }
      };
      this.series = [{
          name: 'Steps',
          data: seriesData
      }];
      this.l_data = false;
    }
  }
};
</script>

<style scoped>
</style>
